import { useAtomValue } from 'jotai';
import { useEffect } from 'react';
import { queryClient } from '~/lib/query';
import { dealershipAtom } from '~/state';
import { getActiveDealershipScope } from './scope';
import { streamServerEvents } from './serverEvents';

// A connection open at least this long is considered healthy, so the reconnect
// backoff resets after it ends. Shorter-lived connections keep backing off,
// which avoids hammering /events behind a flapping proxy that accepts then
// immediately closes the stream.
const STABLE_CONNECTION_MS = 10_000;

/**
 * Holds the realtime SSE connection for the active dealership and dispatches
 * incoming events to the React Query cache. Renders nothing.
 */
export const ServerEventsListener = () => {
  const dealership = useAtomValue(dealershipAtom);
  const dealershipId = dealership?.activeDealershipPerms.dealershipId;

  useEffect(() => {
    // We open a long-lived SSE stream (fetch + ReadableStream) to receive
    // backend cache-invalidation events. This is an external push channel with
    // no library wrapper in our stack — React Query handles request/response
    // fetches, not server push — so an effect is the right tool.
    //
    // Cleanup aborts the in-flight stream and stops the reconnect loop, on
    // unmount or whenever the active dealership changes.
    //
    // Deps are [dealershipId] only: the connection is scoped to one dealership
    // and must be torn down and reopened when that id changes. The scope is
    // re-read from the atom store inside the effect, and queryClient is a
    // module-level singleton, so neither needs listing.
    // Don't open the persistent SSE stream under an automated browser
    // (puppeteer sets navigator.webdriver). A never-closing stream stops
    // puppeteer's `networkidle0` navigations from ever settling, which hangs
    // E2E tests. Realtime isn't exercised by E2E and is unaffected for real
    // users / local dev.
    if (navigator.webdriver) {
      return;
    }
    if (!dealershipId) {
      return;
    }
    const scope = getActiveDealershipScope();
    if (!scope) {
      return;
    }

    const abortController = new AbortController();
    let stopped = false;
    let reconnectDelayMs = 1_000;

    const run = async () => {
      while (!stopped) {
        const startedAt = Date.now();
        try {
          await streamServerEvents(scope, queryClient, abortController.signal);
        } catch {
          // Connection ended (error or server close) — handled by the backoff
          // below. Lossy by design: on reconnect we simply resume; missed
          // events are fine.
        }
        if (stopped) {
          return;
        }
        // Only reset the backoff if the connection stayed open long enough to
        // be healthy; an immediate end keeps growing the delay so we don't
        // hammer /events when the edge is flapping.
        if (Date.now() - startedAt >= STABLE_CONNECTION_MS) {
          reconnectDelayMs = 1_000;
        }
        await new Promise((resolve) => setTimeout(resolve, reconnectDelayMs));
        reconnectDelayMs = Math.min(reconnectDelayMs * 2, 30_000);
      }
    };

    void run();

    return () => {
      stopped = true;
      abortController.abort();
    };
  }, [dealershipId]);

  return null;
};
